import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Renders children only if the current user has WRITE permission for the module.
 * Use for create/update/delete/print/post actions.
 */
export function WithWrite({ moduleCode, children }: { moduleCode: string; children: React.ReactNode }) {
  const { canWrite } = useAuth();
  if (!canWrite(moduleCode)) return null;
  return <>{children}</>;
}

/**
 * Requires authenticated user for protected app routes.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <p className="p-3 text-sm text-gray-500">Laddar...</p>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return <>{children}</>;
}

/**
 * Prevents signed-in users from accessing guest-only routes (for now: /login).
 */
export function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) return <p className="p-3 text-sm text-gray-500">Laddar...</p>;
  if (user) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

/**
 * Requires READ+ permission for a module to access route content.
 */
export function RequireRead({
  moduleCode,
  children,
}: {
  moduleCode: string;
  children: React.ReactNode;
}) {
  const { canRead, loading } = useAuth();

  if (loading) return <p className="p-3 text-sm text-gray-500">Laddar...</p>;
  if (!canRead(moduleCode)) return <Navigate to="/" replace />;

  return <>{children}</>;
}

export function RequireSuperAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="p-3 text-sm text-gray-500">Laddar...</p>;
  if (!user?.isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

